import { gradientsColorsStartRaw } from "../../global/emotion/colors";
import type { GradientDirection } from "../types/gradientDirection.types";
import { RGBAObjectToString } from "../utils/colors/RGBAObjectToString";
import type { RGBAObject } from "../utils/colors/types/RGBAObject.types";
import {
  cssMediaQueryMinWidthDesktop,
  cssMediaQueryMinWidthTablet,
} from "./constants/emotionMediaQueries.constants";

type StyleObject = Record<string, any>;

function gradientStops(
  red: number,
  green: number,
  blue: number,
  direction: string,
): string {
  return `linear-gradient(${direction},
    rgba(${red},${green},${blue},0) 0%,
    rgba(${red},${green},${blue},0.013) 8.1%,
    rgba(${red},${green},${blue},0.049) 15.5%,
    rgba(${red},${green},${blue},0.104) 22.5%,
    rgba(${red},${green},${blue},0.175) 29%,
    rgba(${red},${green},${blue},0.259) 35.3%,
    rgba(${red},${green},${blue},0.352) 41.2%,
    rgba(${red},${green},${blue},0.45) 47.1%,
    rgba(${red},${green},${blue},0.55) 52.9%,
    rgba(${red},${green},${blue},0.648) 58.8%,
    rgba(${red},${green},${blue},0.741) 64.7%,
    rgba(${red},${green},${blue},0.825) 71%,
    rgba(${red},${green},${blue},0.896) 77.5%,
    rgba(${red},${green},${blue},0.951) 84.5%,
    rgba(${red},${green},${blue},0.987) 91.9%,
    rgba(${red},${green},${blue},1))`;
}

export const styleDynamics = {
  backgroundColorFromRGBAObject: (color: RGBAObject): StyleObject => ({
    backgroundColor: RGBAObjectToString(color),
  }),

  backgroundGradient: (options: {
    red: number;
    green: number;
    blue: number;
    direction: GradientDirection;
  }): StyleObject => ({
    backgroundImage: gradientStops(
      options.red,
      options.green,
      options.blue,
      options.direction,
    ),
  }),

  backgroundGradientSpread: (options: {
    red: number;
    green: number;
    blue: number;
    direction: GradientDirection;
    spread: number;
  }): StyleObject => ({
    backgroundImage: `linear-gradient(${options.direction},
      rgba(${options.red},${options.green},${options.blue},0) 0%,
      rgba(${options.red},${options.green},${options.blue},0.013) ${options.spread * 0.081}px,
      rgba(${options.red},${options.green},${options.blue},0.049) ${options.spread * 0.155}px,
      rgba(${options.red},${options.green},${options.blue},0.104) ${options.spread * 0.225}px,
      rgba(${options.red},${options.green},${options.blue},0.175) ${options.spread * 0.29}px,
      rgba(${options.red},${options.green},${options.blue},0.259) ${options.spread * 0.353}px,
      rgba(${options.red},${options.green},${options.blue},0.352) ${options.spread * 0.412}px,
      rgba(${options.red},${options.green},${options.blue},0.45) ${options.spread * 0.471}px,
      rgba(${options.red},${options.green},${options.blue},0.55) ${options.spread * 0.529}px,
      rgba(${options.red},${options.green},${options.blue},0.648) ${options.spread * 0.588}px,
      rgba(${options.red},${options.green},${options.blue},0.741) ${options.spread * 0.647}px,
      rgba(${options.red},${options.green},${options.blue},0.825) ${options.spread * 0.71}px,
      rgba(${options.red},${options.green},${options.blue},0.896) ${options.spread * 0.775}px,
      rgba(${options.red},${options.green},${options.blue},0.951) ${options.spread * 0.845}px,
      rgba(${options.red},${options.green},${options.blue},0.987) ${options.spread * 0.919}px,
      rgba(${options.red},${options.green},${options.blue},1) ${options.spread}px)`,
  }),

  mask: (options: {
    directions: GradientDirection[];
    spreads: number[];
  }): StyleObject => ({
    mask: options.directions
      .map(
        (d, idx) => `linear-gradient(${d},
          rgba(0,0,0,0) 0%,
          rgba(0,0,0,0.013) ${options.spreads[idx] * 0.081}px,
          rgba(0,0,0,0.049) ${options.spreads[idx] * 0.155}px,
          rgba(0,0,0,0.104) ${options.spreads[idx] * 0.225}px,
          rgba(0,0,0,0.175) ${options.spreads[idx] * 0.29}px,
          rgba(0,0,0,0.259) ${options.spreads[idx] * 0.353}px,
          rgba(0,0,0,0.352) ${options.spreads[idx] * 0.412}px,
          rgba(0,0,0,0.45) ${options.spreads[idx] * 0.471}px,
          rgba(0,0,0,0.55) ${options.spreads[idx] * 0.529}px,
          rgba(0,0,0,0.648) ${options.spreads[idx] * 0.588}px,
          rgba(0,0,0,0.741) ${options.spreads[idx] * 0.647}px,
          rgba(0,0,0,0.825) ${options.spreads[idx] * 0.71}px,
          rgba(0,0,0,0.896) ${options.spreads[idx] * 0.775}px,
          rgba(0,0,0,0.951) ${options.spreads[idx] * 0.845}px,
          rgba(0,0,0,0.987) ${options.spreads[idx] * 0.919}px,
          rgba(0,0,0,1) ${options.spreads[idx]}px)`,
      )
      .join(", "),
    maskComposite: "intersect",
  }),

  displayDesktopViewport: (display?: string): StyleObject => ({
    display: "none",
    [cssMediaQueryMinWidthDesktop]: { display: display ?? "inherit" },
  }),

  displayTabletViewport: (display?: string): StyleObject => ({
    display: "none",
    [cssMediaQueryMinWidthTablet]: { display: display ?? "inherit" },
  }),

  hideDesktopViewport: (display?: string): StyleObject => ({
    display: display ?? "inherit",
    [cssMediaQueryMinWidthDesktop]: { display: "none" },
  }),

  hideTabletViewport: (display?: string): StyleObject => ({
    display: display ?? "inherit",
    [cssMediaQueryMinWidthTablet]: { display: "none" },
  }),
};

export const gradientMapToV1 = (gradientId: number): number => {
  if (!!gradientsColorsStartRaw[gradientId]) return gradientId;
  if (!!gradientsColorsStartRaw[gradientId - 100]) return gradientId - 100;
  return 0;
};
